import React, { useState, useEffect, useCallback } from "react";
import {
  Bell,
  Plus,
  Calendar,
  History,
  FileEdit,
  Send,
  Image as ImageIcon,
  X,
  Search,
  Trash2,
  Copy,
  XCircle,
  Users,
  Smartphone,
  Mail,
  Eye,
  Loader2,
  CheckCircle2,
  AlertCircle
} from "lucide-react";
import adminService from "../../../../services/adminService";
import "./Adminnotifications.scss";

const TABS = [
  { key: "overview", label: "Visão Geral", icon: Bell },
  { key: "create", label: "Criar Notificação", icon: Plus },
  { key: "scheduled", label: "Agendadas", icon: Calendar },
  { key: "history", label: "Histórico", icon: History },
  { key: "drafts", label: "Rascunhos", icon: FileEdit },
];

function formatDateTime(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  });
}

const STATUS_LABELS = {
  Draft: "Rascunho",
  Scheduled: "Agendada",
  Sent: "Enviada",
  Cancelled: "Cancelada"
};

export default function AdminNotifications() {
  const [activeTab, setActiveTab] = useState("overview");
  const [editingId, setEditingId] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const goToCreate = (id = null) => {
    setEditingId(id);
    setActiveTab("create");
  };

  return (
    <div className="admin-notifications">
      <div className="admin-notifications__tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              className={activeTab === tab.key ? "active" : ""}
              onClick={() => {
                setActiveTab(tab.key);
                if (tab.key !== "create") setEditingId(null);
              }}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="admin-notifications__body">
        {activeTab === "overview" && (
          <OverviewTab onCreateNew={() => goToCreate(null)} onOpenDetail={setDetailId} />
        )}

        {activeTab === "create" && (
          <CreateTab
            editingId={editingId}
            onSaved={() => setActiveTab("drafts")}
            onSent={() => setActiveTab("history")}
            onScheduled={() => setActiveTab("scheduled")}
          />
        )}

        {activeTab === "scheduled" && (
          <ListTab
            status="agendadas"
            emptyText="Nenhuma notificação agendada no momento."
            onEdit={goToCreate}
            onOpenDetail={setDetailId}
          />
        )}

        {activeTab === "history" && (
          <ListTab
            status="enviadas"
            emptyText="Nenhuma notificação enviada ainda."
            onOpenDetail={setDetailId}
          />
        )}

        {activeTab === "drafts" && (
          <ListTab
            status="rascunhos"
            emptyText="Nenhum rascunho salvo."
            onEdit={goToCreate}
            onOpenDetail={setDetailId}
          />
        )}
      </div>

      {detailId && (
        <DetailModal id={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}

// ─── Visão Geral ──────────────────────────────────────────────────────
function OverviewTab({ onCreateNew, onOpenDetail }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingLaunchEmail, setSendingLaunchEmail] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getNotificationOverview();
      setOverview(data);
    } catch (error) {
      console.error("Erro ao carregar visão geral:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="admin-notifications__overview">
      <div className="admin-notifications__overview-header">
        <h3>Central de Notificações</h3>
      </div>

      <div className="admin-notifications__stat-grid">
        <div className="admin-notifications__stat-card">
          <span className="admin-notifications__stat-label">Total enviadas</span>
          <span className="admin-notifications__stat-value">{loading ? "…" : overview?.totalEnviadas ?? 0}</span>
        </div>
        <div className="admin-notifications__stat-card">
          <span className="admin-notifications__stat-label">Agendadas</span>
          <span className="admin-notifications__stat-value">{loading ? "…" : overview?.agendadas ?? 0}</span>
        </div>
        <div className="admin-notifications__stat-card">
          <span className="admin-notifications__stat-label">Rascunhos</span>
          <span className="admin-notifications__stat-value">{loading ? "…" : overview?.rascunhos ?? 0}</span>
        </div>
        <div className="admin-notifications__stat-card">
          <span className="admin-notifications__stat-label">Taxa de entrega</span>
          <span className="admin-notifications__stat-value">
            {loading ? "…" : `${overview?.taxaEntregaPercent ?? 0}%`}
          </span>
        </div>
      </div>

      <div className="admin-notifications__section-title">Últimas notificações</div>

      {loading && <div className="admin-notifications__loading">Carregando...</div>}

      {!loading && (overview?.ultimas?.length ?? 0) === 0 && (
        <div className="admin-notifications__empty">
          <Bell size={36} />
          <p>Nenhuma notificação criada ainda. Que tal enviar a primeira?</p>
        </div>
      )}

      {!loading && overview?.ultimas?.length > 0 && (
        <div className="admin-notifications__list">
          {overview.ultimas.map((n) => (
            <NotificationRow key={n.id} item={n} onOpenDetail={onOpenDetail} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Listagem genérica (Agendadas / Histórico / Rascunhos) ────────────
function ListTab({ status, emptyText, onEdit, onOpenDetail }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getNotifications(status);
      setItems(data);
    } catch (error) {
      console.error("Erro ao carregar lista:", error);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (id) => {
    if (!window.confirm("Cancelar esta notificação agendada? Ela volta para rascunho.")) return;
    try {
      await adminService.cancelNotification(id);
      await load();
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao cancelar.");
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await adminService.duplicateNotification(id);
      alert("Notificação duplicada como rascunho.");
      await load();
    } catch (error) {
      alert("Erro ao duplicar.");
    }
  };

  const handleResend = async (id) => {
    if (!window.confirm("Reenviar esta notificação exatamente como foi enviada?")) return;
    try {
      await adminService.resendNotification(id);
      alert("Notificação reenviada!");
      await load();
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao reenviar.");
    }
  };

  const handleEditSent = async (id) => {
    try {
      const dup = await adminService.duplicateNotification(id);
      onEdit(dup.id);
    } catch (error) {
      alert("Erro ao preparar edição.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este rascunho permanentemente?")) return;
    try {
      await adminService.deleteNotification(id);
      await load();
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao excluir.");
    }
  };

  if (loading) return <div className="admin-notifications__loading">Carregando...</div>;

  if (items.length === 0) {
    return (
      <div className="admin-notifications__empty">
        <FileEdit size={36} />
        <p>{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="admin-notifications__list">
      {items.map((item) => (
        <NotificationRow
          key={item.id}
          item={item}
          onOpenDetail={onOpenDetail}
          actions={
            <>
              {onEdit && (item.status === "Draft" || item.status === "Scheduled") && (
                <button className="admin-notif-btn admin-notif-btn--edit" onClick={() => onEdit(item.id)}>
                  <FileEdit size={13} /> Editar
                </button>
              )}
              {item.status === "Sent" && (
                <>
                  <button className="admin-notif-btn admin-notif-btn--edit" onClick={() => handleEditSent(item.id)}>
                    <FileEdit size={13} /> Editar
                  </button>
                  <button className="admin-notif-btn admin-notif-btn--resend" onClick={() => handleResend(item.id)}>
                    <Send size={13} /> Reenviar
                  </button>
                </>
              )}
              {item.status === "Scheduled" && (
                <button className="admin-notif-btn admin-notif-btn--cancel" onClick={() => handleCancel(item.id)}>
                  <XCircle size={13} /> Cancelar
                </button>
              )}
              <button className="admin-notif-btn admin-notif-btn--duplicate" onClick={() => handleDuplicate(item.id)}>
                <Copy size={13} /> Duplicar
              </button>
              {item.status === "Draft" && (
                <button className="admin-notif-btn admin-notif-btn--delete" onClick={() => handleDelete(item.id)}>
                  <Trash2 size={13} /> Excluir
                </button>
              )}
            </>
          }
        />
      ))}
    </div>
  );
}

// ─── Linha de notificação (reaproveitada em overview + listas) ────────
function NotificationRow({ item, onOpenDetail, actions }) {
  return (
    <div className="admin-notifications__row">
      <div className="admin-notifications__row-thumb">
        {item.imageUrl
          ? <img src={item.imageUrl} alt="" />
          : <Bell size={18} />}
      </div>

      <div className="admin-notifications__row-info">
        <div className="admin-notifications__row-top">
          <h4>{item.title}</h4>
          <span className={`admin-notifications__badge admin-notifications__badge--${item.status?.toLowerCase()}`}>
            {STATUS_LABELS[item.status] || item.status}
          </span>
        </div>
        <div className="admin-notifications__row-meta">
          <span><Users size={13} /> {item.audience}</span>
          <span>•</span>
          <span>{item.channels}</span>
          {item.status === "Scheduled" && (
            <>
              <span>•</span>
              <span><Calendar size={13} /> {formatDateTime(item.scheduledAt)}</span>
            </>
          )}
          {item.status === "Sent" && (
            <>
              <span>•</span>
              <span>{formatDateTime(item.sentAt)}</span>
              <span>•</span>
              <span>{item.deliveredCount}/{item.totalRecipients} entregues</span>
            </>
          )}
        </div>
      </div>

      <div className="admin-notifications__row-actions">
        <button className="admin-notif-btn admin-notif-btn--view" onClick={() => onOpenDetail(item.id)}>
          <Eye size={13} /> Detalhes
        </button>
        {actions}
      </div>
    </div>
  );
}

// ─── Modal de Detalhes (histórico e prévia geral) ──────────────────────
function DetailModal({ id, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await adminService.getNotificationDetail(id);
        setDetail(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  return (
    <div className="admin-notifications__modal-overlay" onClick={onClose}>
      <div className="admin-notifications__modal admin-notifications__modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="admin-notifications__modal-header">
          <h3>Detalhes da notificação</h3>
          <button className="admin-notifications__modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        {loading && <div className="admin-notifications__loading">Carregando...</div>}

        {!loading && detail && (
          <div className="admin-notifications__modal-body">
            {detail.imageUrl && (
              <img src={detail.imageUrl} alt="" className="admin-notifications__detail-image" />
            )}
            <h4>{detail.title}</h4>
            <p className="admin-notifications__detail-message">{detail.message}</p>

            <div className="admin-notifications__detail-meta">
              <span><strong>Status:</strong> {STATUS_LABELS[detail.status] || detail.status}</span>
              <span><strong>Canais:</strong> {detail.channels}</span>
              <span><strong>Público:</strong> {detail.audience}</span>
              {detail.scheduledAt && <span><strong>Agendada para:</strong> {formatDateTime(detail.scheduledAt)}</span>}
              {detail.sentAt && <span><strong>Enviada em:</strong> {formatDateTime(detail.sentAt)}</span>}
              <span><strong>Total de destinatários:</strong> {detail.totalRecipients}</span>
              <span><strong>Entregues:</strong> {detail.deliveredCount}</span>
              <span><strong>Falhas:</strong> {detail.failedCount}</span>
            </div>

            {detail.recipients?.length > 0 && (
              <>
                <div className="admin-notifications__section-title">Destinatários</div>
                <div className="admin-notifications__recipient-list">
                  {detail.recipients.map((r) => (
                    <div key={r.userId} className="admin-notifications__recipient-row">
                      <span>{r.name} <small>({r.email})</small></span>
                      <span className="admin-notifications__recipient-status">
                        {r.pushDelivered && <Smartphone size={13} className="ok" title="Push entregue" />}
                        {r.emailDelivered && <Mail size={13} className="ok" title="E-mail entregue" />}
                        {(r.pushError || r.emailError) && (
                          <AlertCircle size={13} className="fail" title={r.pushError || r.emailError} />
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Criar / Editar Notificação ─────────────────────────────────────────
const EMPTY_FORM = {
  title: "",
  message: "",
  imageUrl: null,
  imagePublicId: null,
  channels: { push: true, email: false },
  audienceMode: "group",
  userGroups: { freelancers: true, companies: false },
  planFilter: "todos",
  specificSelected: [], // [{id, name, email, type}]
};

function CreateTab({ editingId, onSaved, onSent, onScheduled }) {
  const [id, setId] = useState(editingId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loadingDetail, setLoadingDetail] = useState(!!editingId);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [saving, setSaving] = useState(null); // "draft" | "test" | "now" | "schedule"
  const [error, setError] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");

  const [specificSearch, setSpecificSearch] = useState("");
  const [specificResults, setSpecificResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    setId(editingId);
    if (!editingId) {
      setForm(EMPTY_FORM);
      return;
    }

    (async () => {
      setLoadingDetail(true);
      try {
        const detail = await adminService.getNotificationDetail(editingId);
        setForm({
          title: detail.title,
          message: detail.message,
          imageUrl: detail.imageUrl,
          imagePublicId: null,
          channels: {
            push: detail.channelsList.includes("push"),
            email: detail.channelsList.includes("email"),
          },
          audienceMode: detail.audienceModeRaw,
          userGroups: {
            freelancers: detail.userGroupsList.includes("freelancers"),
            companies: detail.userGroupsList.includes("companies"),
          },
          planFilter: detail.planFilterRaw,
          specificSelected: detail.recipients?.map(r => ({
            id: r.userId, name: r.name, email: r.email
          })) || [],
        });
        setScheduledAt(
          detail.scheduledAt ? detail.scheduledAt.slice(0, 16) : ""
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingDetail(false);
      }
    })();
  }, [editingId]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const result = await adminService.uploadNotificationImage(file);
      updateField("imageUrl", result.url);
      updateField("imagePublicId", result.publicId);
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao enviar imagem.");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSpecificSearch = async (value) => {
    setSpecificSearch(value);
    if (value.trim().length < 2) {
      setSpecificResults([]);
      return;
    }

    setSearching(true);
    try {
      const [freelancers, companies, admins] = await Promise.all([
        adminService.getFreelancers("todos", value),
        adminService.getCompanies("todos", value),
        adminService.getAdmins("todos", value),
      ]);

      const combined = [
        ...freelancers.map(u => ({ ...u, type: "Freelancer" })),
        ...companies.map(u => ({ ...u, type: "Empresa" })),
        ...admins.map(u => ({ ...u, type: "Admin" })),
      ].filter(u => !form.specificSelected.some(s => s.id === u.id));

      setSpecificResults(combined);
    } catch (error) {
      console.error(error);
    } finally {
      setSearching(false);
    }
  };

  const addSpecific = (user) => {
    updateField("specificSelected", [...form.specificSelected, {
      id: user.id, name: user.name, email: user.email, type: user.type
    }]);
    setSpecificResults(specificResults.filter(u => u.id !== user.id));
  };

  const removeSpecific = (userId) => {
    updateField("specificSelected", form.specificSelected.filter(u => u.id !== userId));
  };

  const buildDto = (action) => {
    const channels = [];
    if (form.channels.push) channels.push("push");
    if (form.channels.email) channels.push("email");

    const userGroups = [];
    if (form.userGroups.freelancers) userGroups.push("freelancers");
    if (form.userGroups.companies) userGroups.push("companies");

    return {
      title: form.title,
      message: form.message,
      imageUrl: form.imageUrl,
      imagePublicId: form.imagePublicId,
      channels,
      audienceMode: form.audienceMode,
      userGroups,
      planFilter: form.planFilter,
      specificUserIds: form.specificSelected.map(u => u.id),
      action,
      scheduledAt: action === "schedule" && scheduledAt
        ? new Date(scheduledAt).toISOString()
        : null,
    };
  };

  const validate = () => {
    if (!form.title.trim() || !form.message.trim()) {
      return "Título e mensagem são obrigatórios.";
    }
    if (!form.channels.push && !form.channels.email) {
      return "Selecione ao menos um canal: Push ou E-mail.";
    }
    if (form.audienceMode === "group" && !form.userGroups.freelancers && !form.userGroups.companies) {
      return "Selecione ao menos um público: Freelancers ou Empresas.";
    }
    if (form.audienceMode === "specific" && form.specificSelected.length === 0) {
      return "Selecione ao menos um destinatário.";
    }
    return "";
  };

  const persist = async (action) => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return null;
    }
    setError("");

    const dto = buildDto(action);
    const result = id
      ? await adminService.updateNotification(id, dto)
      : await adminService.createNotification(dto);

    if (!id) setId(result.id);
    return result.id;
  };

  const handleSaveDraft = async () => {
    setSaving("draft");
    try {
      const savedId = await persist("draft");
      if (savedId) {
        alert("Rascunho salvo.");
        onSaved?.();
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Erro ao salvar rascunho.");
    } finally {
      setSaving(null);
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm("Enviar esta notificação agora para todos os destinatários selecionados?")) return;
    setSaving("now");
    try {
      const savedId = await persist("send_now");
      if (savedId) {
        alert("Notificação enviada!");
        onSent?.();
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Erro ao enviar.");
    } finally {
      setSaving(null);
    }
  };

  const handleSchedule = async () => {
    if (!scheduledAt) {
      setError("Escolha data e hora para agendar.");
      return;
    }
    setSaving("schedule");
    try {
      const savedId = await persist("schedule");
      if (savedId) {
        alert("Notificação agendada!");
        onScheduled?.();
      }
    } catch (error) {
      setError(error?.response?.data?.message || "Erro ao agendar.");
    } finally {
      setSaving(null);
    }
  };

  const handleSendTest = async () => {
    if (!id) {
      setError("Salve como rascunho antes de enviar um teste.");
      return;
    }
    setSaving("test");
    try {
      await adminService.sendTestNotification(id);
      alert("Teste enviado para você.");
    } catch (error) {
      alert(error?.response?.data?.message || "Erro ao enviar teste.");
    } finally {
      setSaving(null);
    }
  };
  
  if (loadingDetail) return <div className="admin-notifications__loading">Carregando...</div>;

  return (
    <div className="admin-notifications__create">
      <div className="admin-notifications__create-grid">
        {/* ─── Formulário ─── */}
        <div className="admin-notifications__form">
          <label>
            Título
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="Ex: Já estamos no ar! 🚀"
              maxLength={80}
            />
          </label>

          <label>
            Mensagem
            <textarea
              rows={4}
              value={form.message}
              onChange={(e) => updateField("message", e.target.value)}
              placeholder="Escreva a mensagem que a pessoa vai ver..."
              maxLength={500}
            />
          </label>

          <label>
            Ícone / imagem (opcional)
            <div className="admin-notifications__image-upload">
              {form.imageUrl && (
                <div className="admin-notifications__image-preview">
                  <img src={form.imageUrl} alt="" />
                  <button type="button" onClick={() => { updateField("imageUrl", null); updateField("imagePublicId", null); }}>
                    <X size={14} />
                  </button>
                </div>
              )}
              <label className="admin-notif-btn admin-notif-btn--upload">
                {uploadingImage ? <Loader2 size={14} className="spin" /> : <ImageIcon size={14} />}
                {uploadingImage ? "Enviando..." : (form.imageUrl ? "Trocar imagem" : "Enviar imagem")}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleImageUpload} />
              </label>
            </div>
          </label>

          <div className="admin-notifications__form-section">
            <span className="admin-notifications__form-section-label">Canais de envio</span>
            <div className="admin-notifications__checkbox-row">
              <label className="admin-notifications__checkbox">
                <input
                  type="checkbox"
                  checked={form.channels.push}
                  onChange={(e) => updateField("channels", { ...form.channels, push: e.target.checked })}
                />
                <Smartphone size={14} /> Push (app/navegador)
              </label>
              <label className="admin-notifications__checkbox">
                <input
                  type="checkbox"
                  checked={form.channels.email}
                  onChange={(e) => updateField("channels", { ...form.channels, email: e.target.checked })}
                />
                <Mail size={14} /> E-mail
              </label>
            </div>
          </div>

          <div className="admin-notifications__form-section">
            <span className="admin-notifications__form-section-label">Público</span>
            <div className="admin-notifications__audience-tabs">
              <button
                type="button"
                className={form.audienceMode === "group" ? "active" : ""}
                onClick={() => updateField("audienceMode", "group")}
              >
                Por grupo
              </button>
              <button
                type="button"
                className={form.audienceMode === "specific" ? "active" : ""}
                onClick={() => updateField("audienceMode", "specific")}
              >
                Selecionar pessoas
              </button>
            </div>

            {form.audienceMode === "group" ? (
              <>
                <div className="admin-notifications__checkbox-row">
                  <label className="admin-notifications__checkbox">
                    <input
                      type="checkbox"
                      checked={form.userGroups.freelancers}
                      onChange={(e) => updateField("userGroups", { ...form.userGroups, freelancers: e.target.checked })}
                    />
                    Freelancers
                  </label>
                  <label className="admin-notifications__checkbox">
                    <input
                      type="checkbox"
                      checked={form.userGroups.companies}
                      onChange={(e) => updateField("userGroups", { ...form.userGroups, companies: e.target.checked })}
                    />
                    Empresas
                  </label>
                </div>

                <select
                  className="admin-notifications__select"
                  value={form.planFilter}
                  onChange={(e) => updateField("planFilter", e.target.value)}
                >
                  <option value="todos">Todos os planos</option>
                  <option value="gold">Somente Gold</option>
                  <option value="free">Somente Free</option>
                </select>
              </>
            ) : (
              <div className="admin-notifications__specific-picker">
                <div className="admin-notifications__specific-search">
                  <Search size={14} />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou e-mail..."
                    value={specificSearch}
                    onChange={(e) => handleSpecificSearch(e.target.value)}
                  />
                </div>

                {searching && <div className="admin-notifications__loading">Buscando...</div>}

                {specificResults.length > 0 && (
                  <div className="admin-notifications__specific-results">
                    {specificResults.map((u) => (
                      <button key={u.id} type="button" onClick={() => addSpecific(u)}>
                        <span>{u.name}</span>
                        <small>{u.type} • {u.email}</small>
                      </button>
                    ))}
                  </div>
                )}

                {form.specificSelected.length > 0 && (
                  <div className="admin-notifications__specific-chips">
                    {form.specificSelected.map((u) => (
                      <span key={u.id} className="admin-notifications__chip">
                        {u.name}
                        <button type="button" onClick={() => removeSpecific(u.id)}><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="admin-notifications__form-section">
            <span className="admin-notifications__form-section-label">Agendar envio (opcional)</span>
            <input
              type="datetime-local"
              className="admin-notifications__select"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
            />
          </div>

          {error && <div className="admin-notifications__form-error"><AlertCircle size={14} /> {error}</div>}

          <div className="admin-notifications__form-actions">
            <button className="admin-notif-btn admin-notif-btn--draft" disabled={!!saving} onClick={handleSaveDraft}>
              {saving === "draft" ? <Loader2 size={14} className="spin" /> : <FileEdit size={14} />}
              Salvar rascunho
            </button>
            <button className="admin-notif-btn admin-notif-btn--test" disabled={!!saving || !id} onClick={handleSendTest} title={!id ? "Salve como rascunho primeiro" : ""}>
              {saving === "test" ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
              Enviar teste
            </button>
            <button className="admin-notif-btn admin-notif-btn--schedule" disabled={!!saving || !scheduledAt} onClick={handleSchedule}>
              {saving === "schedule" ? <Loader2 size={14} className="spin" /> : <Calendar size={14} />}
              Agendar
            </button>
            <button className="admin-notif-btn admin-notif-btn--primary" disabled={!!saving} onClick={handleSendNow}>
              {saving === "now" ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
              Enviar agora
            </button>
          </div>
        </div>

        {/* ─── Prévia ─── */}
        <div className="admin-notifications__preview">
          <span className="admin-notifications__form-section-label">Prévia</span>
          <div className="admin-notifications__preview-card">
            <div className="admin-notifications__preview-icon">
              {form.imageUrl ? <img src={form.imageUrl} alt="" /> : <Bell size={16} />}
            </div>
            <div className="admin-notifications__preview-text">
              <strong>{form.title || "Título da notificação"}</strong>
              <p>{form.message || "A mensagem aparece aqui..."}</p>
            </div>
          </div>
          <p className="admin-notifications__preview-hint">
            Prévia aproximada — a aparência final varia por navegador/SO no push, e por cliente de e-mail (Gmail, Outlook) no e-mail.
          </p>
        </div>
      </div>
    </div>
  );
}